import { Badge, BadgeProps, Button } from '@purpur/library';
import { IconCheckCircleFilled } from '@purpur/library/icon/check-circle-filled';
import styled from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

import { AdaptiveDisneyIcon } from '../components/AdaptiveDisneyIcon/AdaptiveDisneyIcon';
import { AdaptiveHboMaxIcon } from '../components/AdaptiveHboMaxIcon/AdaptiveHboMaxIcon';
import { AdaptiveIcon } from '../components/AdaptiveIcon/AdaptiveIcon';
import { AdaptiveImage } from '../components/AdaptiveImage/AdaptiveImage';
import { AdaptiveNetflixIcon } from '../components/AdaptiveNetflixIcon/AdaptiveNetflixIcon';
import { CopyLocalCode } from '../components/CopyLocalCode/CopyLocalCode';
import { PriceTag } from '../components/PriceTag';
import { sideSpacingOfEntries } from '../OrderCart.styles';

export const WithTooltip = styled.span`
    display: flex;
    align-items: center;
    gap: ${getPurpurSpacing('50')};
    height: ${getPurpurSpacing('300')};
`;
export const CellIcon = styled(AdaptiveIcon)`
    grid-area: icon;
    ${purpurMedia('lg')`
        margin-bottom: auto;
    `};
`;
export const CellImage = styled(AdaptiveImage)`
    grid-area: image;
    ${purpurMedia('lg')`
        margin-bottom: auto;
    `}
`;
export const CellTitle = styled.span`
    grid-column: title;
    font-weight: ${getPurpurTypography('weightBold')};
    word-break: break-word;
`;
export const CellPrice = styled(PriceTag)`
    grid-column: price;
    font-weight: ${getPurpurTypography('weightBold')};
    margin-left: auto;
`;
export const CellPriceText = styled.span`
    grid-column: price;
    font-weight: ${getPurpurTypography('weightBold')};
    margin-left: auto;
    white-space: nowrap;
`;
export const CellTerm = styled.span`
    grid-column: term;
`;
export const CellDetail = styled.div`
    grid-column: detail;
    &:empty {
        display: none;
    }
`;
export const CellDetailSecondary = styled(CellDetail)`
    color: ${getPurpurColor('textMedium')};
    font-size: ${getPurpurTypography('scale75')};
`;
export const RemoveButton = styled(Button)`
    grid-column: 1/-1;
    ${purpurMedia('lg')`
        grid-column: 2/-1;
    `}
`;
export const Separator = styled.div`
    grid-column: separator;
    width: 100%;
    margin: ${getPurpurSpacing('150')} 0;
    border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};

    ${purpurMedia('lg')`
        margin: ${getPurpurSpacing('100')} 0;
        border: none;
    `}
`;

export const GiftBadge = styled(Badge).attrs<Partial<BadgeProps>>({ variant: 'attention', showIcon: false })`
    grid-area: badge;
    background-color: ${getPurpurColor('teal500')};
    color: ${getPurpurColor('functionalWhite')};
    height: ${getPurpurSpacing('300')};
    width: fit-content;
`;

export const FootNote = styled.span`
    grid-column: footnote;
    font-size: ${getPurpurTypography('scale75')};
    color: ${getPurpurColor('textMedium')};
`;
export const LocalCode = styled(CopyLocalCode)`
    grid-column: localcode;
    margin-bottom: ${getPurpurSpacing('100')};
`;
export const DiscountPrice = styled(CellPrice)`
    color: ${getPurpurColor('textStatusDiscount')};
`;

export const EntryListItem = styled.div.attrs({ role: 'listitem' })`
    ${sideSpacingOfEntries};
    ${purpurMedia('sm')`
        padding-top: ${getPurpurSpacing('200')};
        padding-bottom: ${getPurpurSpacing('200')};
    `}
    ${purpurMedia('lg')`
        padding-top: ${getPurpurSpacing('300')};
        padding-bottom: ${getPurpurSpacing('300')};
    `}
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderMedium')};

    ${CellTerm}, ${CellTerm}+${CellPrice} {
        margin-bottom: ${getPurpurSpacing('100')};
    }
    ${CellPrice}:last-child, ${CellTerm}:last-child, ${CellTerm}:has( + ${CellPrice}:last-child) {
        margin-bottom: unset;
    }
`;

export const InsuranceEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: start;
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
        'icon title price'
        'detail detail detail';
    column-gap: ${getPurpurSpacing('100')};
    row-gap: ${getPurpurSpacing('150')};

    ${purpurMedia('lg')`
        grid-template-areas:
                'icon title price' 
                'icon detail detail';
        column-gap: ${getPurpurSpacing('300')};
        row-gap: ${getPurpurSpacing('200')};
    `}
`;

export const SimCardEntryListItem = styled(EntryListItem)`
    font-weight: ${getPurpurTypography('weightBold')};
    display: grid;
    align-items: center;
    grid-template-columns: auto 1fr auto;
    gap: ${getPurpurSpacing('100')};

    ${purpurMedia('lg')`
        grid-template-columns: auto 1fr auto;
        gap: ${getPurpurSpacing('300')};
    `}
`;

export const UpsellEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: start;
    align-items: center;
    grid-template-columns: auto 1fr auto;
    column-gap: ${getPurpurSpacing('100')};
    grid-template-areas:
        'image title title'
        'separator separator separator'
        'term term price'
        'detail detail detail'
        'action action action';
    ${RemoveButton} {
        margin-top: ${getPurpurSpacing('50')};
    }

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
        grid-template-areas:
            'image title title'
            'image separator separator'
            'image term price'
            'image detail detail'
            'image action action';
    `}
`;

export const GiftEntryListItem = styled(EntryListItem)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: ${getPurpurSpacing('100')};
    grid-template-areas:
        'image title title'
        'image detail detail'
        'image badge badge'
        'separator separator separator'
        'term term price';

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
        grid-template-areas:
            'image title badge'
            'image detail detail'
            'image separator separator'
            'image term price';
    `}

    ${CellDetailSecondary} {
        margin-top: ${getPurpurSpacing('50')};
        margin-bottom: ${getPurpurSpacing('100')};
    }
`;

export const MainProductEntryListItem = styled(EntryListItem)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: ${getPurpurSpacing('100')};
    grid-template-areas:
        'image title title'
        'image detail detail'
        'separator separator separator'
        'term term price'
        'localcode localcode localcode'
        'footnote footnote footnote';

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
        grid-template-areas:
            'image title title'
            'image detail detail'
            'image separator separator'
            'image term price'
            'image localcode localcode'
            'image footnote footnote';
    `}

    ${CellDetailSecondary} {
        margin-top: ${getPurpurSpacing('50')};
    }
`;

export const PromotionEntryListItem = styled(EntryListItem)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: ${getPurpurSpacing('100')};
    grid-template-areas:
        'icon title title'
        'badge badge badge'
        'separator separator separator'
        'term term price';

    ${purpurMedia('lg')`
        grid-template-areas:
            'icon title badge'
            'icon separator separator'
            'icon term price';
         column-gap: ${getPurpurSpacing('300')};
    `}

    ${GiftBadge} {
        margin-top: ${getPurpurSpacing('100')};
        ${purpurMedia('lg')`
            margin-top: unset;
        `}
    }
`;

export const DeliveryEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: left;
    grid-template-areas: 'icon title price';
    grid-template-columns: auto 1fr auto;
    gap: ${getPurpurSpacing('100')};
`;
export const DeliveryEntryCellTitle = styled(CellTitle)`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${getPurpurSpacing('100')};
`;

export const CellPictogram = styled.div`
    grid-area: pictogram;
    ${purpurMedia('lg')`
        margin-bottom: auto;
    `};
`;
export const ServiceEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: start;
    align-items: center;
    column-gap: ${getPurpurSpacing('100')};
    row-gap: ${getPurpurSpacing('100')};
    grid-auto-columns: auto 1fr;
    grid-template-areas: 'pictogram title price';

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
        row-gap: ${getPurpurSpacing('100')};
    `}
    &:has(${CellDetail}:not(:empty)) {
        grid-template-areas:
            'pictogram title price'
            'detail detail detail';

        ${purpurMedia('lg')`
            grid-template-areas:
                'pictogram title price'
                'pictogram detail detail';
        `};
    }
`;
export const ServiceEntryCellDetail = styled(CellDetail)`
    margin-top: ${getPurpurSpacing('100')};
    &:not(:empty) + & {
        margin-top: 0;
    }
`;

export const VasSwitch = styled(Button)`
    grid-column: 1/-1;
    ${purpurMedia('lg')`
        grid-column: 2/-1;
    `}
`;
export const NetflixIcon = styled(AdaptiveNetflixIcon)`
    grid-area: icon;
`;
export const HboMaxIcon = styled(AdaptiveHboMaxIcon)`
    grid-area: icon;
`;
export const DisneyIcon = styled(AdaptiveDisneyIcon)`
    grid-area: icon;
`;

export const VasEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: start;
    align-items: center;
    column-gap: ${getPurpurSpacing('100')};
    row-gap: ${getPurpurSpacing('100')};
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 'icon title price';

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
    `}

    &:has(${CellDetail}:not(:empty)) {
        grid-template-areas:
            'icon title price'
            'detail detail detail';

        ${purpurMedia('lg')`
            grid-template-areas:
                'icon title price'
                'icon detail detail';
        `};
    }

    ${CellPrice}, ${CellPriceText} {
        grid-row: price;
    }
`;

export const HboMaxVasEntryListItem = styled(VasEntryListItem)`
    grid-template-areas: 'icon title price';
`;

export const MobRatePlanEntryListItem = styled(EntryListItem)`
    display: grid;
    column-gap: ${getPurpurSpacing('100')};
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
        'icon title price'
        'separator separator separator'
        'detail detail detail';

    ${purpurMedia('lg')`
        grid-template-areas:
            'icon title price'
            'icon separator separator'
            'icon detail detail';
        column-gap: ${getPurpurSpacing('300')};
    `}

    ${Separator} {
        margin: 0;
        height: ${getPurpurSpacing('150')};
        border: none;
        ${purpurMedia('lg')`
            height: ${getPurpurSpacing('100')};
        `}
    }
`;
export const Row = styled(CellDetail)`
    margin-top: ${getPurpurSpacing('50')};
    ${purpurMedia('lg')`
        margin-top: ${getPurpurSpacing('100')};
    `}
`;
export const RowSecondary = styled(Row)`
    color: ${getPurpurColor('textMedium')};
    font-size: ${getPurpurTypography('scale75')};
`;
export const RowPhone = styled(Row)`
    margin-top: ${getPurpurSpacing('200')};
`;
export const RowPhoneValue = styled(RowSecondary)`
    margin-top: ${getPurpurSpacing('50')};
    ${purpurMedia('lg')`
        margin-top: ${getPurpurSpacing('50')};
    `}
`;

export const DiscountAppliedEntryListItem = styled(EntryListItem)`
    display: flex;
    gap: ${getPurpurSpacing('150')};
`;

export const DiscountForm = styled.form`
    display: grid;
    row-gap: ${getPurpurSpacing('300')};
    white-space: break-spaces;

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('200')};
        grid-template-columns: 1fr 8.25rem;
        > button { 
            margin-top: 1.375rem;
            margin-bottom: auto;
        }
    `};
`;

export const CheckIcon = styled(IconCheckCircleFilled).attrs({ size: 'md' })`
    flex-shrink: 0;
    color: ${getPurpurColor('textStatusSuccessMedium')};
`;

export const TvPlanEntryListItem = styled(EntryListItem)`
    display: grid;
    justify-items: start;
    align-items: center;
    column-gap: ${getPurpurSpacing('100')};
    grid-auto-columns: auto 1fr;
    grid-template-areas: 'icon title price';

    ${purpurMedia('lg')`
        column-gap: ${getPurpurSpacing('300')};
    `}
    &:has(${CellDetail}:not(:empty)) {
        grid-template-areas:
            'icon title price'
            'detail detail detail';

        ${purpurMedia('lg')`
            grid-template-areas:
                'icon title price'
                'icon detail detail';
        `};
    }
`;

export const FuturePriceRow = styled(CellDetail)`
    color: ${getPurpurColor('textMedium')};
    font-size: ${getPurpurTypography('scale75')};
    margin-top: ${getPurpurSpacing('200')};

    ${purpurMedia('lg')`
        font-size: ${getPurpurTypography('scale100')};
    `}
`;

export const FuturePriceRowContent = styled.span``;

export const TvPlanCommitmentText = styled(RowSecondary)`
    margin-top: ${getPurpurSpacing('50')};

    ${purpurMedia('lg')`
        margin-top: ${getPurpurSpacing('100')};
    `};
`;
